import { Catalog, catalogImport } from './catalog';
import { ConsoleState, docsSamples, templates } from './store';
import { GroupTemplate } from './types';

export interface DocsSyncResult {
  added: string[];
  updated: string[];
  removed: string[];
  kept: string[];
}

/**
 * What the docs say about endpoints a vendor already took: their address and their examples.
 * Anything a person set up (a mapping, a destination, their own samples) is left alone, and a
 * sample's "should be refused" mark survives its example being refreshed.
 */
const refreshAttached = (state: ConsoleState, fresh: GroupTemplate[]): ConsoleState => {
  let routes = state.routes;
  let samples = state.samples;
  for (const group of state.groups) {
    const template = fresh.find((item) => item.id === group.templateId);
    if (!template) continue;
    for (const member of template.members) {
      /* found by its id, or by its name when the docs moved it and the id moved with the address */
      const routeId = routes.find(
        (route) =>
          route.groupId === group.id && (route.id === `${group.id}-${member.slug}` || route.name === member.name),
      )?.id;
      if (!routeId) continue;
      routes = routes.map((route) =>
        route.id === routeId && member.path
          ? { ...route, ingress: { ...route.ingress, path: member.path, method: member.method } }
          : route,
      );
      const marks = new Map(
        samples.filter((sample) => sample.routeId === routeId).map((sample) => [sample.id, sample] as const),
      );
      const refreshed = docsSamples(routeId, member).map((sample) => {
        const held = marks.get(sample.id);
        return held?.expectation
          ? { ...sample, expectation: held.expectation, expectedViolationPath: held.expectedViolationPath }
          : sample;
      });
      samples = [...samples.filter((sample) => sample.routeId !== routeId || sample.source !== 'docs'), ...refreshed];
      const route = routes.find((item) => item.id === routeId);
      if (route && !samples.some((sample) => sample.id === route.canarySampleId) && refreshed[0])
        routes = routes.map((item) => (item.id === routeId ? { ...item, canarySampleId: refreshed[0].id } : item));
    }
  }
  return { ...state, routes, samples };
};

export const syncFromDocs = (state: ConsoleState, catalog: Catalog): { state: ConsoleState; result: DocsSyncResult } => {
  const brought = catalogImport(catalog.groups);
  const fresh = new Set(brought.templates.map((template) => template.id));
  const held = templates(state);
  const used = (id: string) => state.groups.some((group) => group.templateId === id);
  const dropped = held.filter((template) => !fresh.has(template.id));
  const kept = dropped.filter((template) => used(template.id));

  return {
    state: {
      ...refreshAttached(state, brought.templates),
      templates: [...kept, ...brought.templates],
      schemas: [...state.schemas.filter((schema) => !brought.schemas.some((item) => item.id === schema.id)), ...brought.schemas],
      authSchemes: [
        ...(state.authSchemes ?? []),
        ...brought.authSchemes.filter((scheme) => !state.authSchemes?.some((item) => item.id === scheme.id)),
      ],
    },
    result: {
      added: brought.templates.filter((template) => !held.some((item) => item.id === template.id)).map((t) => t.name),
      updated: brought.templates.filter((template) => held.some((item) => item.id === template.id)).map((t) => t.name),
      removed: dropped.filter((template) => !used(template.id)).map((template) => template.name),
      kept: kept.map((template) => template.name),
    },
  };
};
